import colors from '../../../../constants/appColors'
import type { ColorSurface, DeltaTone, GameOutcome } from '../types'

/** Web's dark-card colour for each slot. */
const DARK_SERIES = [
  colors.CORAL,
  colors.ANOTHER_BLUE,
  colors.GREEN_APPLE,
  colors.STAT_PINK,
  colors.VIOLET_200,
  colors.STAT_LIME,
  colors.WHITE,
  colors.STAT_FUCHSIA,
]

/** Web's white slot, which would vanish on a white card. */
const WHITE_SLOT = 6

/**
 * Line colours for the charted stats, by colour slot — one per slot up to
 * `MAX_SELECTED_STATS`, so two selected stats never share one.
 *
 * No tenant primary (SportNinja amber, Canlan's web and mobile blues) or
 * anything near one, so a stat never reads as a button. Checked in OKLab:
 * every pair at least ΔE 14 apart (the first six, ΔE 15+), every colour at
 * least ΔE 13 from each primary.
 *
 * Both platforms use the same colours (a stat looks the same on web and
 * mobile); only web's white slot becomes near-black on mobile's white card.
 * On white the bright lines are lighter-contrast (cyan, lime under 2:1) —
 * chosen to match web.
 */
export const SERIES_PALETTES: Record<ColorSurface, string[]> = {
  dark: DARK_SERIES,
  light: DARK_SERIES.map((color, slot) =>
    slot === WHITE_SLOT ? colors.BLACK_PEARL : color
  ),
}

/**
 * Result text colour by outcome. Web's bright pair reads on its dark rows;
 * on white `SUCCESS_200` is only 2.5:1, so the light surface takes the darker
 * ramp steps.
 */
export const RESULT_PALETTES: Record<
  ColorSurface,
  Record<GameOutcome, string>
> = {
  dark: {
    W: colors.SUCCESS_200,
    L: colors.ERROR_200,
    T: colors.NEUTRAL_50,
  },
  light: {
    W: colors.SUCCESS_400,
    L: colors.ERROR_300,
    T: colors.NEUTRAL_100,
  },
}

/**
 * Comparison delta colour by tone: the same green / red as a win / loss on
 * each surface, so better and worse read the way results do.
 */
export const DELTA_PALETTES: Record<ColorSurface, Record<DeltaTone, string>> = {
  dark: {
    positive: colors.SUCCESS_200,
    negative: colors.ERROR_200,
  },
  light: {
    positive: colors.SUCCESS_400,
    negative: colors.ERROR_300,
  },
}
